import type { ProfileState, WireEarnedAchievement } from './net/protocol';
import { achievements, getAchievement, getTitle, titles, type TitleId } from './sim/achievements';
import { createUiWindow } from './ui/window';

export interface ProfileGump {
  setProfile(profile: ProfileState): void;
  setUnavailable(): void;
  award(achievement: WireEarnedAchievement): void;
  setOpen(open: boolean): void;
  isOpen(): boolean;
}

export interface ProfileGumpOptions {
  onOpenChange?: (open: boolean) => void;
}

/** General player-facing achievements and equipped-title window. */
export function initProfileGump(
  doc: Document,
  selectTitle: (id: TitleId | null) => void,
  options: ProfileGumpOptions = {},
): ProfileGump {
  const button = doc.createElement('button');
  button.id = 'profile-button';
  button.type = 'button';
  button.textContent = '★';
  button.title = 'Profile & Achievements';
  button.setAttribute('aria-label', button.title);
  const toolbar = doc.getElementById('toolbar');
  toolbar?.insertBefore(button, doc.getElementById('wardrobe-button'));

  const win = createUiWindow({
    doc,
    id: 'profile-gump',
    title: 'Profile & Achievements',
    ariaLabel: 'Profile and Achievements',
    fallbackWidth: 430,
    fallbackHeight: 480,
    escapeCloses: true,
    clampToViewport: true,
    onOpen: () => {
      button.setAttribute('aria-expanded', 'true');
      options.onOpenChange?.(true);
    },
    onClose: () => {
      button.setAttribute('aria-expanded', 'false');
      options.onOpenChange?.(false);
    },
  });
  if (!win) {
    return {
      setProfile: () => {},
      setUnavailable: () => {},
      award: () => {},
      setOpen: () => {},
      isOpen: () => false,
    };
  }
  const content = doc.createElement('div');
  content.id = 'profile-gump-content';
  win.root.append(content);

  let profile: ProfileState | null = null;
  let unavailable = false;
  let pendingTitle: TitleId | null | undefined;
  const render = (): void => {
    content.replaceChildren();
    if (unavailable) {
      const copy = doc.createElement('p');
      copy.textContent = 'Profile is unavailable. Reconnect and try again.';
      content.append(copy);
      return;
    }
    if (profile === null) {
      const copy = doc.createElement('p');
      copy.textContent = 'Loading profile…';
      content.append(copy);
      return;
    }
    if (!profile.reserved) {
      const copy = doc.createElement('p');
      copy.textContent = 'Reserve your name to earn achievements and equip titles.';
      content.append(copy);
      return;
    }
    const earned = new Map(profile.achievements.map((entry) => [entry.id, entry]));
    const titleHeading = doc.createElement('h3');
    titleHeading.textContent = 'Titles';
    content.append(titleHeading);
    const choices: { id: TitleId | null; text: string }[] = [{ id: null, text: 'No title' }];
    for (const definition of titles) {
      if (earned.has(definition.achievementId)) {
        choices.push({ id: definition.id, text: definition.text });
      }
    }
    for (const choice of choices) {
      const label = doc.createElement('label');
      label.style.display = 'block';
      const radio = doc.createElement('input');
      radio.type = 'radio';
      radio.name = 'profile-title';
      radio.checked =
        pendingTitle !== undefined ? pendingTitle === choice.id : profile.selectedTitleId === choice.id;
      radio.disabled = pendingTitle !== undefined;
      radio.addEventListener('change', () => {
        pendingTitle = choice.id;
        render();
        selectTitle(choice.id);
      });
      label.append(radio, doc.createTextNode(` ${choice.text}`));
      content.append(label);
    }
    if (pendingTitle !== undefined) {
      const pending = doc.createElement('p');
      pending.id = 'profile-title-pending';
      pending.textContent = 'Saving title…';
      content.append(pending);
    }
    const achievementHeading = doc.createElement('h3');
    achievementHeading.textContent = 'Achievements';
    content.append(achievementHeading);
    for (const definition of achievements) {
      const item = doc.createElement('article');
      const grant = earned.get(definition.id);
      const name = doc.createElement('strong');
      name.textContent = `${grant ? 'Earned — ' : 'Locked — '}${definition.name}`;
      const description = doc.createElement('p');
      description.textContent = definition.description;
      item.append(name, description);
      if (definition.titleReward) {
        const reward = doc.createElement('p');
        reward.textContent = `Title reward: ${getTitle(definition.titleReward)?.text ?? definition.titleReward}`;
        item.append(reward);
      }
      if (grant) {
        const evidence = doc.createElement('small');
        const facts = [
          new Date(grant.earnedAt).toLocaleString(),
          grant.evidence?.board
            ? `Board ${grant.evidence.board.partition}`
            : undefined,
          grant.evidence?.season !== undefined ? `Season ${grant.evidence.season}` : undefined,
          grant.evidence?.rank !== undefined ? `Rank ${grant.evidence.rank}` : undefined,
          grant.evidence?.score !== undefined ? `Score ${grant.evidence.score}` : undefined,
          grant.evidence?.counter !== undefined && grant.evidence?.value !== undefined
            ? `${grant.evidence.counter} ${grant.evidence.value}`
            : undefined,
        ].filter(Boolean);
        evidence.textContent = facts.join(' · ');
        item.append(evidence);
      }
      content.append(item);
    }
  };
  render();
  button.addEventListener('click', () => (win.isOpen() ? win.close() : win.open()));
  return {
    setProfile(next) {
      profile = next;
      unavailable = false;
      pendingTitle = undefined;
      render();
    },
    setUnavailable() {
      unavailable = true;
      pendingTitle = undefined;
      render();
    },
    award(achievement) {
      // Toasts share one fixed top-centre stack so a burst of awards reads as a
      // list instead of overlapping; each removes itself after its CSS fade.
      let stack = doc.getElementById('achievement-awards');
      if (!stack) {
        stack = doc.createElement('div');
        stack.id = 'achievement-awards';
        doc.body.append(stack);
      }
      const toast = doc.createElement('div');
      toast.className = 'achievement-award-notification';
      toast.setAttribute('role', 'status');
      const definition = getAchievement(achievement.id);
      toast.textContent = `Achievement earned: ${definition?.name ?? achievement.id}`;
      stack.append(toast);
      doc.defaultView?.setTimeout(() => toast.remove(), 6000);
    },
    setOpen(open) {
      if (open) win.open();
      else win.close();
    },
    isOpen: win.isOpen,
  };
}
